"use client"

import * as React from "react"
import { AlertCircle, CheckCircle, CircleX, Info, XCircle } from "lucide-react"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Toast } from "@/components/ui/toast"
import { UndoToast } from "@/components/ui/undo-toast"

type AlertType = "success" | "error" | "warning" | "info"

/** Dialog-only types. "destructive" gives the shared dangerous-action
 *  styling (red CircleX icon, red icon background, red CTA) — use it for
 *  every delete/remove/discard confirm so they all look the same. */
type DialogType = AlertType | "destructive"

interface ToastOptions {
    message: string
    type?: AlertType
    duration?: number
}

interface AlertDialogOptions {
    title?: string
    message: string
    type?: DialogType
    confirmText?: string
    cancelText?: string
    /** Visual style for the primary (confirm) button. Defaults to "default";
     *  pass "destructive" for a red CTA when the confirm action is dangerous. */
    confirmVariant?: "default" | "destructive"
    /** Extra classes appended to the confirm button — use to override the
     *  variant's color (e.g. matching the composer Stop red). */
    confirmClassName?: string
    /** Custom icon node, rendered in place of the type-derived icon. */
    icon?: React.ReactNode
    /** Tailwind bg class for the icon circle, overriding the type default. */
    iconBgClassName?: string
    thirdButtonText?: string
    onConfirm?: () => void
    onCancel?: () => void
    onThird?: () => void
}

interface PromptDialogOptions {
    title?: string
    message?: string
    placeholder?: string
    defaultValue?: string
    confirmText?: string
    cancelText?: string
}

interface UndoToastOptions {
    message: string
    duration?: number
    /** Omit to render the toast without an Undo button — useful for
     *  truly-destructive completions where you still want bottom-left
     *  confirmation feedback. */
    onUndo?: () => void | Promise<void>
}

interface AlertDialogContextType {
    showAlert: (options: AlertDialogOptions) => void
    showConfirm: (options: AlertDialogOptions) => Promise<"confirm" | "cancel" | "third">
    showPrompt: (options: PromptDialogOptions) => Promise<string | null>
    showToast: (options: ToastOptions) => void
    showUndoToast: (options: UndoToastOptions) => void
}

const AlertDialogContext = React.createContext<AlertDialogContextType | undefined>(undefined)

export function AlertDialogProvider({ children }: { children: React.ReactNode }) {
    const [isOpen, setIsOpen] = React.useState(false)
    // Undo-toast state lives up here so the dialog-opening callbacks below
    // (showAlert / showConfirm / showPrompt) can dismiss lingering undo
    // toasts — once the user starts a new action that needs confirmation,
    // the previous actions' undo affordances are stale. Held as a list so
    // back-to-back destructive actions (e.g. deleting several rows) each
    // get their own toast, stacked vertically rather than clobbering.
    const [undoToasts, setUndoToasts] = React.useState<(UndoToastOptions & { id: number })[]>([])
    const [options, setOptions] = React.useState<AlertDialogOptions>({
        message: "",
        type: "info",
        confirmText: "OK"
    })
    const [resolvePromise, setResolvePromise] = React.useState<((value: "confirm" | "cancel" | "third") => void) | null>(null)

    // Prompt dialog state
    const [isPromptOpen, setIsPromptOpen] = React.useState(false)
    const [promptOptions, setPromptOptions] = React.useState<PromptDialogOptions>({})
    const [promptValue, setPromptValue] = React.useState("")
    const [resolvePromptPromise, setResolvePromptPromise] = React.useState<((value: string | null) => void) | null>(null)

    const showAlert = React.useCallback((opts: AlertDialogOptions) => {
        setUndoToasts([])
        setOptions({
            type: "info",
            confirmText: "OK",
            ...opts
        })
        setIsOpen(true)
    }, [])

    const showConfirm = React.useCallback((opts: AlertDialogOptions): Promise<"confirm" | "cancel" | "third"> => {
        return new Promise(resolve => {
            setUndoToasts([])
            setOptions({
                type: "warning",
                confirmText: "Confirm",
                cancelText: "Cancel",
                ...opts
            })
            setResolvePromise(() => resolve)
            setIsOpen(true)
        })
    }, [])

    const [toast, setToast] = React.useState<ToastOptions | null>(null)
    const [toastKey, setToastKey] = React.useState(0)

    const showToast = React.useCallback((opts: ToastOptions) => {
        setToast(opts)
        setToastKey(k => k + 1)
    }, [])

    const showUndoToast = React.useCallback((opts: UndoToastOptions) => {
        // Date.now() can collide for two synchronous calls — add a counter
        // so every toast keeps a stable, unique React key.
        setUndoToasts(prev => [...prev, { ...opts, id: Date.now() + prev.length }])
    }, [])

    const showPrompt = React.useCallback((opts: PromptDialogOptions): Promise<string | null> => {
        return new Promise(resolve => {
            setUndoToasts([])
            setPromptOptions({
                confirmText: "OK",
                cancelText: "Cancel",
                ...opts
            })
            setPromptValue(opts.defaultValue || "")
            setResolvePromptPromise(() => resolve)
            setIsPromptOpen(true)
        })
    }, [])

    const handleConfirm = () => {
        options.onConfirm?.()
        if (resolvePromise) {
            resolvePromise("confirm")
            setResolvePromise(null)
        }
        setIsOpen(false)
    }

    const handleCancel = () => {
        options.onCancel?.()
        if (resolvePromise) {
            resolvePromise("cancel")
            setResolvePromise(null)
        }
        setIsOpen(false)
    }

    const handleThird = () => {
        options.onThird?.()
        if (resolvePromise) {
            resolvePromise("third")
            setResolvePromise(null)
        }
        setIsOpen(false)
    }

    const handlePromptConfirm = () => {
        if (resolvePromptPromise) {
            resolvePromptPromise(promptValue)
            setResolvePromptPromise(null)
        }
        setIsPromptOpen(false)
    }

    const handlePromptCancel = () => {
        if (resolvePromptPromise) {
            resolvePromptPromise(null)
            setResolvePromptPromise(null)
        }
        setIsPromptOpen(false)
    }

    const getIcon = () => {
        if (options.icon) return options.icon
        switch (options.type) {
            case "success":
                return <CheckCircle className="w-5 h-5 text-accent-green-text" />
            case "error":
                return <XCircle className="w-5 h-5 text-danger" />
            case "destructive":
                return <CircleX className="w-6 h-6 text-danger" />
            case "warning":
                return <AlertCircle className="w-5 h-5 text-accent-yellow-text" />
            case "info":
            default:
                return <Info className="w-5 h-5 text-accent-blue" />
        }
    }

    const getIconBgColor = () => {
        if (options.iconBgClassName) return options.iconBgClassName
        switch (options.type) {
            case "success":
                return "bg-accent-green-bg"
            case "error":
                return "bg-danger-bg"
            case "destructive":
                return "bg-danger-bg"
            case "warning":
                return "bg-accent-yellow-bg"
            case "info":
            default:
                return "bg-accent-purple-bg"
        }
    }

    const getDefaultTitle = () => {
        switch (options.type) {
            case "success":
                return "Success"
            case "error":
                return "Error"
            case "warning":
                return "Warning"
            case "info":
            default:
                return "Information"
        }
    }

    return (
        <AlertDialogContext.Provider value={{ showAlert, showConfirm, showPrompt, showToast, showUndoToast }}>
            {children}
            <Dialog open={isOpen} onOpenChange={setIsOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full ${getIconBgColor()} flex items-center justify-center flex-shrink-0`}>
                                {getIcon()}
                            </div>
                            <DialogTitle className="text-lg">{options.title || getDefaultTitle()}</DialogTitle>
                        </div>
                        <DialogDescription className="text-sm text-gray-600 mt-4 whitespace-pre-wrap">{options.message}</DialogDescription>
                    </DialogHeader>

                    <div className="flex justify-end gap-2 mt-6">
                        {options.thirdButtonText && (
                            <Button variant="ghost" onClick={handleThird}>
                                {options.thirdButtonText}
                            </Button>
                        )}
                        {options.cancelText && (
                            <Button variant="outline" onClick={handleCancel}>
                                {options.cancelText}
                            </Button>
                        )}
                        <Button
                            variant={options.confirmVariant ?? (options.type === "destructive" ? "destructive" : undefined)}
                            // No className override needed: shadcn's
                            // --destructive token is aliased to --danger
                            // in globals.css, so Button variant="destructive"
                            // already paints with the brand salmon hex.
                            className={options.confirmClassName}
                            onClick={handleConfirm}
                        >
                            {options.confirmText}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Prompt Dialog */}
            <Dialog open={isPromptOpen} onOpenChange={setIsPromptOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-accent-purple-bg flex items-center justify-center flex-shrink-0">
                                <Info className="w-5 h-5 text-accent-blue" />
                            </div>
                            <DialogTitle className="text-lg">{promptOptions.title || "Enter Value"}</DialogTitle>
                        </div>
                        {promptOptions.message && (
                            <DialogDescription className="text-sm text-gray-600 mt-4">{promptOptions.message}</DialogDescription>
                        )}
                    </DialogHeader>

                    <input
                        type="text"
                        value={promptValue}
                        onChange={e => setPromptValue(e.target.value)}
                        onKeyDown={e => {
                            if (e.key === "Enter") handlePromptConfirm()
                        }}
                        placeholder={promptOptions.placeholder}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent mt-4"
                        autoFocus
                    />

                    <div className="flex justify-end gap-2 mt-6">
                        <Button variant="outline" onClick={handlePromptCancel}>
                            {promptOptions.cancelText}
                        </Button>
                        <Button onClick={handlePromptConfirm}>{promptOptions.confirmText}</Button>
                    </div>
                </DialogContent>
            </Dialog>

            {toast && (
                <Toast
                    key={toastKey}
                    message={toast.message}
                    type={toast.type ?? "info"}
                    duration={toast.duration ?? 4000}
                    onClose={() => setToast(null)}
                />
            )}

            {/* Stack toasts bottom-up: newest sits at the resting position
                (24px), each older one lifts 48px higher. */}
            {undoToasts.map((t, i) => (
                <UndoToast
                    key={t.id}
                    message={t.message}
                    duration={t.duration ?? 5000}
                    stackOffset={24 + (undoToasts.length - 1 - i) * 48}
                    onUndo={t.onUndo ? () => { void t.onUndo!() } : undefined}
                    onClose={() => setUndoToasts(prev => prev.filter(x => x.id !== t.id))}
                />
            ))}
        </AlertDialogContext.Provider>
    )
}

export function useAlertDialog() {
    const context = React.useContext(AlertDialogContext)
    if (!context) {
        throw new Error("useAlertDialog must be used within an AlertDialogProvider")
    }
    return context
}
